import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { CardHead, ErrorBanner } from '../../components/ui/Primitives.js';
import { useOwnTrunks } from '../../lib/api/hooks-products.js';
import type { Trunk } from '@arcnet/contracts';
import { CARRIER_PRESETS, toE164, type CarrierPreset } from './carrierPresets.js';
import { TrunkTest } from './TrunkTest.js';

/**
 * A client's own trunk, step by step (owner, 26 September 2026: "adding a trunk on a clients side
 * didn't go well. It needs to be easier" -- a step-by-step wizard, chosen the same day).
 *
 *   Carrier   pick one; everything it dictates comes from its preset (carrierPresets.ts)
 *   Login     what the carrier's welcome email gives: username and password
 *   Number    the number calls show, which for most carriers is the username
 *   Connect   saved, then watched until the switch says registered -- or the carrier's refusal,
 *             with the SIP messages, when it does not
 *
 * Every other setting keeps its preset and stays on the trunk's full form (Configure), for the
 * carrier that needs something unusual.
 */

type Step = 'carrier' | 'login' | 'number' | 'connect';
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'carrier', label: 'Carrier' }, { key: 'login', label: 'Login' },
  { key: 'number', label: 'Number' }, { key: 'connect', label: 'Connect' },
];

const OTHER: CarrierPreset = {
  key: 'other', label: 'Other', hint: 'Any SIP carrier',
  host: '', port: 5060, transport: 'udp', auth_mode: 'register', realm: null,
  outbound_format: 'national', codecs: ['PCMA', 'PCMU'],
  username_label: 'Username', username_hint: 'As your carrier gave it', username_is_number: false,
};

export function OwnTrunkWizard({ tenantId, existing, onDone }: {
  tenantId: string; existing: string[]; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('carrier');
  const [preset, setPreset] = useState<CarrierPreset | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('5060');
  const [transport, setTransport] = useState<'udp' | 'tcp'>('udp');
  const [realm, setRealm] = useState('');
  const [registers, setRegisters] = useState(true);
  const [number, setNumber] = useState('');
  const [createdId, setCreatedId] = useState<string | null>(null);

  const other = preset?.key === 'other';
  const e164 = toE164(number);
  const name = (() => {
    const base = (preset?.key === 'other' ? (host.split('.').slice(-3, -1).join('-') || 'carrier') : preset?.key ?? 'trunk').toLowerCase();
    let n = base; let i = 2;
    while (existing.includes(n)) n = `${base}-${i++}`;
    return n;
  })();

  const loginOk = username.trim() !== '' && password !== '' && (!other || (host.trim() !== '' && Number(port) > 0));

  const create = useMutation({
    mutationFn: () => {
      const p = preset!;
      return api.post<Trunk>(`/v1/tenants/${tenantId}/own-trunks`, {
        provider_name: other ? (host.trim().split('.').slice(-2, -1)[0] ?? host.trim()) : p.label,
        name, direction: 'both',
        host: other ? host.trim() : p.host,
        port: other ? Number(port) : p.port,
        transport: other ? transport : p.transport,
        auth_mode: other ? (registers ? 'register' : 'digest') : p.auth_mode,
        auth_username: username.trim(),
        auth_secret: password,
        ...((other ? realm.trim() : p.realm) ? { auth_realm: other ? realm.trim() : p.realm } : {}),
        outbound_format: p.outbound_format,
        allowed_codecs: p.codecs,
        ...(e164 ? { fallback_cli: e164 } : {}),
      });
    },
    onSuccess: (t) => {
      setCreatedId(t.id);
      void qc.invalidateQueries({ queryKey: ['own-trunks', tenantId] });
    },
  });

  // Watch the new trunk until the switch has an answer: every 3 s for up to 2 minutes.
  const own = useOwnTrunks(tenantId);
  const created = own.data?.data.find((t) => t.id === createdId) ?? null;
  const reg = created?.live?.reg_state ?? null;
  const [waited, setWaited] = useState(0);
  const settled = reg === 'REGISTERED_STATE' || reg === 'WRONG_CREDENTIALS_STATE' || reg === 'REGISTRAR_ERROR_STATE'
    || reg === 'REGISTER_TIMEOUT_STATE' || waited >= 40 || (created !== null && created.auth_mode !== 'register' && waited >= 10);
  useEffect(() => {
    if (!createdId || settled) return;
    const t = setTimeout(() => { setWaited((w) => w + 1); void qc.invalidateQueries({ queryKey: ['own-trunks', tenantId] }); }, 3_000);
    return () => clearTimeout(t);
  }, [createdId, settled, waited, qc, tenantId]);

  const pick = (p: CarrierPreset) => {
    setPreset(p);
    if (p.key === 'other') { setHost(''); setRealm(''); setRegisters(true); }
    setStep('login');
  };
  const idx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Add your own trunk" action={
        <div className="arc-row" style={{ gap: 6 }}>
          {STEPS.map((s, i) => (
            <span key={s.key} className={i === idx ? '' : 'arc-subtle'} style={{ fontSize: 12.5, fontWeight: i === idx ? 600 : 400 }}>
              {i + 1}. {s.label}{i < STEPS.length - 1 ? ' ·' : ''}
            </span>
          ))}
        </div>} />
      <div className="arc-card-body">
        {step === 'carrier' && (
          <>
            <p className="arc-muted" style={{ marginTop: 0, fontSize: 13 }}>Who is your SIP account with?</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {[...CARRIER_PRESETS, OTHER].map((p) => (
                <button key={p.key} type="button" className="arc-btn" onClick={() => pick(p)}
                  style={{ height: 'auto', padding: '12px 14px', justifyContent: 'flex-start', textAlign: 'left' }}>
                  <span className="arc-stack" style={{ gap: 2 }}>
                    <strong>{p.label}</strong>
                    <span className="arc-subtle" style={{ fontSize: 12 }}>{p.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'login' && preset && (
          <div className="arc-stack" style={{ gap: 12, maxWidth: 420 }}>
            <p className="arc-muted" style={{ margin: 0, fontSize: 13 }}>
              {other ? 'The details your carrier gave you.' : `The login from your ${preset.label} welcome email.`}
            </p>
            {other && (
              <>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor="wz-host">Server</label>
                  <input id="wz-host" className="arc-input arc-mono" value={host} placeholder="sip.carrier.co.za"
                    onChange={(e) => setHost(e.target.value)} autoComplete="off" />
                </div>
                <div className="arc-row" style={{ gap: 10 }}>
                  <div className="arc-field" style={{ margin: 0, width: 110 }}>
                    <label className="arc-label" htmlFor="wz-port">Port</label>
                    <input id="wz-port" className="arc-input arc-num" inputMode="numeric" value={port}
                      onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} />
                  </div>
                  <div className="arc-field" style={{ margin: 0, width: 130 }}>
                    <label className="arc-label" htmlFor="wz-tr">Transport</label>
                    <select id="wz-tr" className="arc-select" value={transport} onChange={(e) => setTransport(e.target.value as 'udp' | 'tcp')}>
                      <option value="udp">UDP</option><option value="tcp">TCP</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="wz-user">{preset.username_label}</label>
              <input id="wz-user" className="arc-input arc-mono" value={username} autoComplete="off"
                onChange={(e) => setUsername(e.target.value)} />
              <div className="arc-hint">{preset.username_hint}</div>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="wz-pass">Password</label>
              <input id="wz-pass" className="arc-input arc-mono" type="password" value={password} autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)} />
            </div>
            {other && (
              <>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor="wz-realm">Realm or domain <span className="arc-subtle">(if given)</span></label>
                  <input id="wz-realm" className="arc-input arc-mono" value={realm} placeholder={host || 'the server'}
                    onChange={(e) => setRealm(e.target.value)} autoComplete="off" />
                </div>
                <label className="arc-row" style={{ gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={registers} onChange={(e) => setRegisters(e.target.checked)} />
                  Register with the carrier (almost always: it is how incoming calls find you)
                </label>
              </>
            )}
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setStep('carrier')}>Back</button>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!loginOk}
                onClick={() => { if (!number && preset.username_is_number) setNumber(username.trim()); setStep('number'); }}>
                Next
              </button>
            </div>
          </div>
        )}

        {step === 'number' && preset && (
          <div className="arc-stack" style={{ gap: 12, maxWidth: 420 }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="wz-num">Your number with {preset.label === 'Other' ? 'the carrier' : preset.label}</label>
              <input id="wz-num" className="arc-input arc-mono" value={number} placeholder="087 265 0584"
                onChange={(e) => setNumber(e.target.value)} />
              <div className={number && !e164 ? 'arc-error' : 'arc-hint'}>
                {number && !e164 ? 'Not a phone number.'
                  : 'Shown to the people you call when an extension has no number of its own.'}
              </div>
            </div>
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setStep('login')}>Back</button>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!!number && !e164}
                onClick={() => setStep('connect')}>Next</button>
            </div>
          </div>
        )}

        {step === 'connect' && preset && !createdId && (
          <div className="arc-stack" style={{ gap: 12, maxWidth: 480 }}>
            <dl className="arc-kv" style={{ margin: 0 }}>
              <dt>Carrier</dt><dd>{preset.label}{other ? ` (${host.trim()}:${port})` : ''}</dd>
              <dt>{preset.username_label}</dt><dd className="arc-mono">{username.trim()}</dd>
              <dt>Password</dt><dd>••••••••</dd>
              <dt>Number</dt><dd className="arc-mono">{e164 ?? '—'}</dd>
            </dl>
            <p className="arc-muted" style={{ margin: 0, fontSize: 12.5 }}>
              Your outside calls move to this trunk once it is registered.
            </p>
            {create.error && <ErrorBanner error={create.error} />}
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setStep('number')} disabled={create.isPending}>Back</button>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={create.isPending}
                onClick={() => create.mutate()}>
                {create.isPending ? 'Saving…' : 'Connect'}
              </button>
            </div>
          </div>
        )}

        {step === 'connect' && createdId && (
          <div className="arc-stack" style={{ gap: 10 }}>
            {!settled && (
              <div className="arc-row" style={{ gap: 8, fontSize: 13 }}>
                <Icon name="clock" size={14} />
                <span>{reg && reg !== 'NOT_LOADED' ? 'Registering with the carrier…' : 'Sending it to the switch…'}</span>
                <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>{waited * 3} s</span>
              </div>
            )}
            {settled && reg === 'REGISTERED_STATE' && (
              <div className="arc-banner arc-banner-info" role="status">
                <Icon name="check" size={14} />
                <span style={{ flex: 1 }}>Registered with {!preset || other ? 'your carrier' : preset.label}. Outside calls now use this trunk.</span>
              </div>
            )}
            {settled && reg !== 'REGISTERED_STATE' && (
              <>
                <div className="arc-banner arc-banner-warn" role="status">
                  <Icon name="warning" size={14} />
                  <span style={{ flex: 1 }}>
                    {created?.auth_mode !== 'register' ? 'Saved. This trunk does not register; the test below checks the login.'
                      : 'Not registered yet. The test below shows what the carrier said.'}
                  </span>
                </div>
                <TrunkTest trunkId={createdId} tenantId={tenantId} authMode={created?.auth_mode ?? 'register'} autoRun />
              </>
            )}
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>
                {settled ? 'Done' : 'Close, keep checking in the background'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
